import { Canvas, FabricImage } from 'fabric';
import { generateUniqueId } from '../../shared/utils/generate-id';
import { applyVideoControls } from './video-controls';

type RegisterVideo = (id: string, element: HTMLVideoElement) => void;

const loadVideoElement = (url: string): Promise<HTMLVideoElement> =>
  new Promise((resolve, reject) => {
    const el = document.createElement('video');
    el.src = url;
    el.crossOrigin = 'anonymous';
    el.preload = 'auto';
    el.addEventListener('canplaythrough', () => resolve(el), { once: true });
    el.addEventListener('error', () => reject(new Error(`Failed to load video: ${url}`)), { once: true });
  });

/** For each FabricImage with `videoSrc`, creates and registers an HTMLVideoElement. */
export const hydrateCanvasVideos = async (canvas: Canvas, registerVideo: RegisterVideo): Promise<void> => {
  const candidates = canvas.getObjects().filter((o): o is FabricImage => {
    if (!(o instanceof FabricImage)) return false;
    return typeof (o as { videoSrc?: unknown }).videoSrc === 'string';
  });

  if (candidates.length === 0) return;

  await Promise.all(
    candidates.map(async obj => {
      const url = (obj as { videoSrc?: string }).videoSrc!;
      const wantAudio = !!(obj as { audioEnabled?: boolean }).audioEnabled;

      const existing = obj.getElement();
      if (existing instanceof HTMLVideoElement && existing.src === url) {
        existing.muted = !wantAudio;
        return;
      }

      const videoEl = await loadVideoElement(url);
      videoEl.muted = !wantAudio;
      videoEl.width = videoEl.videoWidth;
      videoEl.height = videoEl.videoHeight;

      const videoId = generateUniqueId('video');
      obj.setElement(videoEl);
      obj.set('id', videoId);
      applyVideoControls(obj);
      registerVideo(videoId, videoEl);

      videoEl.currentTime = 0;
    })
  );

  canvas.requestRenderAll();
};
